import { createFileRoute } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-store'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { TaskCard, LeadCard, ThisWeekCard, PaymentScheduleCard, ActivityFeedCard, OperationsCards, AnnouncementCard, SenderSuggestionsCard } from '@/components/dashboard'
import { dashboardSummaryQueryOptions } from '@/components/dashboard/dashboard-queries'
import { StripeAccountWarnings } from '@/components/shared/stripe-account-warnings'
import { GmailAccountWarnings } from '@/components/shared/gmail-account-warnings'
import { organizationStore } from '@/stores/organization.store'
import { dashboardSettingsStore } from '@/stores/dashboard-settings.store'
import { getMeQueryOptions } from '@/stores/user.store'
import { canPreloadOrganizationQueries } from '@/lib/route-preloading'
import type { GetMeResponse } from '@/gen/cortado/v1/users_pb'

export const Route = createFileRoute('/_app/_home/dashboard')({
  component: DashboardPage,
  loader: ({ context }) => {
    if (!canPreloadOrganizationQueries(context.auth)) return
    const organizationId = organizationStore.state.selectedOrganizationId
    if (!organizationId) return

    // GetMe is already ensured by the parent _app beforeLoad, so this reads
    // from cache synchronously rather than firing a request.
    const currentUserId = context.queryClient.getQueryData<GetMeResponse>(getMeQueryOptions().queryKey)?.user?.id

    // The widgets request the user's persisted windows (useDashboardSummary
    // reads the same store), so the prefetch has to match them or it warms a
    // cache entry nothing reads and the widgets fire a second request.
    const { leadsRecencyDays, upcomingPaymentsDaysAhead } = dashboardSettingsStore.state

    // Fire-and-forget: warms the cache for when the widgets mount, without
    // blocking navigation on the round-trip.
    void context.queryClient.ensureQueryData(
      dashboardSummaryQueryOptions({
        organizationId,
        currentUserId,
        leadsRecencyDays,
        upcomingPaymentsDaysAhead,
      })
    )
  },
})

function DashboardPage() {
  // Automatically sets breadcrumbs based on route configuration
  useBreadcrumbFromRoute()
  // Read the org id from the store rather than the GetOrganization response —
  // it's the same value, available a full network tier earlier.
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {selectedOrganizationId && <StripeAccountWarnings organizationId={selectedOrganizationId} />}
      {selectedOrganizationId && <GmailAccountWarnings organizationId={selectedOrganizationId} />}
      {selectedOrganizationId && <AnnouncementCard organizationId={selectedOrganizationId} />}
      {selectedOrganizationId && <SenderSuggestionsCard organizationId={selectedOrganizationId} />}
      <OperationsCards />
      <ThisWeekCard />

      <div className="grid auto-rows-min gap-4 md:grid-cols-3">
        <LeadCard
          title="Current Leads"
          description="New and In Progress leads that are currently being worked on"
        />
        <TaskCard />

        <PaymentScheduleCard
          title="Overdue Invoices"
          description="Overdue and upcoming payments"
        />
      </div>

      <div className="w-full">
        <ActivityFeedCard />
      </div>
      <div className="flex-1 rounded-xl md:min-h-min" />
    </div>
  )
}
